import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd, NavigationStart, Event as RouterEvent } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { takeUntil, debounceTime, distinctUntilChanged, filter, take } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { RegisteredRetailer } from 'src/app/models/registered-retailer.model';
import { RetailerService } from 'src/app/service/retailer.service';
import { RetailerFilter } from 'src/app/models/retailer-filters.model';
import { RetailerDataStoreService } from 'src/app/service/retailer-data-store.service';
import { LocationService, GeoLocation } from 'src/app/service/location.service';

@Component({
  selector: 'app-agro-dealer-list',
  templateUrl: './agro-dealer-list.component.html',
  styleUrls: ['./agro-dealer-list.component.scss']
})
export class AgroDealerListComponent implements OnInit, OnDestroy {
  searchTerm: string = '';
  showMyDealers: boolean = false;

  dealers: RegisteredRetailer[] = [];
  totalDealers: number = 0;
  pageIndex: number = 0;
  pageSize: number = 10;
  isLoading: boolean = false;
  hasMoreData: boolean = true;

  currentLatitude: number | null = null;
  currentLongitude: number | null = null;
  public isLocationLoading: boolean = false;

  private destroy$ = new Subject<void>();
  private searchInput$ = new Subject<string>();
  private routerEventsSubscription: Subscription | null = null;

  private scrollListener: ((event: Event) => void) | null = null;

  private currentNavigationTriggerType: 'imperative' | 'popstate' | 'hashchange' | null = null;

  // NEW: Flag to ensure loadDealers is called only once for the very first time
  private initialDealerLoadDone: boolean = false;


  constructor(
    private router: Router,
    private retailerService: RetailerService,
    private snackBar: MatSnackBar,
    private retailerDataStore: RetailerDataStoreService,
    private locationService: LocationService
  ) { }

  ngOnInit(): void {
    this.retailerDataStore.clearRetailer();
    this.retailerDataStore.setNavigateToLandingPage(false);

    // Order matters: Set up router events first, then location updates
    // The router events will handle subsequent loads.
    this.subscribeToRouterEvents();
    this.subscribeToDataStoreRefresh();
    this.subscribeToSearchInput();
    this.addScrollListener();

    // NEW: Location updates will trigger the VERY FIRST load of dealers
    this.subscribeToLocationUpdates();
    console.log('AgroDealerListComponent: Component initialized. Waiting for location for initial load.');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.routerEventsSubscription) {
      this.routerEventsSubscription.unsubscribe();
      this.routerEventsSubscription = null;
      console.log('Router events subscription unsubscribed in ngOnDestroy.');
    }

    this.removeScrollListener();
  }

  private subscribeToRouterEvents(): void {
    this.routerEventsSubscription = this.router.events.pipe(
      filter((event: RouterEvent): event is NavigationStart | NavigationEnd =>
        event instanceof NavigationStart || event instanceof NavigationEnd
      )
    ).subscribe((event: NavigationStart | NavigationEnd) => {
      if (event instanceof NavigationStart) {
        if (event.url.includes('/dealers')) {
          this.currentNavigationTriggerType = event.navigationTrigger || null;
          console.log(`AgroDealerListComponent: NavigationStart to /dealers. Trigger: ${this.currentNavigationTriggerType}`);
          this.addScrollListener();
        } else {
          this.removeScrollListener();
        }
      }

      if (event instanceof NavigationEnd) {
        if (event.urlAfterRedirects.includes('/dealers')) {
          console.log('AgroDealerListComponent: NavigationEnd to /dealers detected.');
          this.retailerDataStore.clearRetailer();
          this.retailerDataStore.setNavigateToLandingPage(false);
          // NEW LOGIC: Only trigger loadDealers for subsequent imperative navigations
          // The very first load is now handled by subscribeToLocationUpdates
          if (this.currentNavigationTriggerType === 'imperative' && this.initialDealerLoadDone) {
            console.log('AgroDealerListComponent: Imperative navigation (subsequent). Reloading dealers.');
            this.loadDealers(false);
          } else if (this.currentNavigationTriggerType === 'popstate' || this.currentNavigationTriggerType === 'hashchange') {
            console.log('AgroDealerListComponent: Popstate/Hashchange navigation. Relying on preserved state.');
          }
          this.currentNavigationTriggerType = null; // Reset for next navigation
        }
      }
    });
  }

  private subscribeToDataStoreRefresh(): void {
    this.retailerDataStore.dealerDataRefresh$
      .pipe(takeUntil(this.destroy$))
      .subscribe(shouldRefresh => {
        if (shouldRefresh) {
          console.log('AgroDealerListComponent: Data store refresh signal received - reloading dealers.');
          this.loadDealers(false);
        }
      });
  }

  private subscribeToSearchInput(): void {
    this.searchInput$.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.applySearch();
    });
  }

  // MODIFIED: This now handles the very first load of dealers
  private subscribeToLocationUpdates(): void {
    this.isLocationLoading = true;
    if(this.locationService.locationInProgress$) {
      this.locationService.locationInProgress$.pipe(
        takeUntil(this.destroy$)
      ).subscribe((locationInProgress) => {
        this.isLocationLoading = locationInProgress;
      });
    }
    if(this.locationService.currentLocationError$) {
      this.locationService.currentLocationError$.pipe(
        filter(errorCode => errorCode !== null),
        takeUntil(this.destroy$)
      ).subscribe(errorCode => {
        console.error(`AgroDealerListComponent: Location error occurred. Error code: ${errorCode}`);
        switch (errorCode) {
          case 1:
            this.snackBar.open('Location permission denied. Please enable it in your browser settings.', 'Dismiss', { duration: 7000 });
            break;
          case 2:
            this.snackBar.open('Location information is unavailable. Check your device settings.', 'Dismiss', { duration: 5000 });
            break;
          case 3:
            this.snackBar.open('Failed to get location. Please check if location is enabled.', 'Dismiss', { duration: 5000 });
            break;
          default:
            this.snackBar.open('An unknown error occurred while getting location.', 'Dismiss', { duration: 5000 });
            break;
        }
        this.isLocationLoading = false;
        this.loadDealers(false);
      });
    }
    this.locationService.currentLocation$
      .pipe(
        // NEW: Take only 1 non-null/error value for the initial load
        // This ensures loadDealers is called only once for the initial location
        distinctUntilChanged((prev, curr) => {
          // Treat null to non-null as a distinct change
          if (prev === null && curr !== null) return false;
          // Treat non-null to null as a distinct change
          if (prev !== null && curr === null) return false;
          // If both are non-null, compare exact coordinates (or with a small threshold if needed)
          if (prev !== null && curr !== null) {
            return prev.latitude === curr.latitude && prev.longitude === curr.longitude;
          }
          // If both are null, they are not distinct
          return true;
        }),
        filter(location => {
          // If location is null, and initial load isn't done, allow to pass to trigger load without filter
          if (location === null && !this.initialDealerLoadDone) {
            console.log('AgroDealerListComponent: Initial load not done, passing null location.');
            return false;
          }
          // If location is not null, and initial load isn't done, allow to pass
          if (location !== null && !this.initialDealerLoadDone) {
            return true;
          }
          // For subsequent updates, only pass if location is not null
          return location !== null;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(
        (location: GeoLocation | null) => {
          this.isLocationLoading = false;
          this.currentLatitude = location ? location.latitude : null;
          this.currentLongitude = location ? location.longitude : null;
          if (!this.initialDealerLoadDone) {
            console.log('Calling loadDealers for the first time');
            this.loadDealers(false);
            this.initialDealerLoadDone = true;
          }

          // Show snackbar only if location becomes unavailable AFTER the initial load,
          // or if dealers are already loaded and location state changes to null.
          if (!location && this.initialDealerLoadDone && (this.dealers.length > 0 || !this.isLoading)) {
            this.snackBar.open('Location unavailable. Showing dealers without proximity filter.', 'Dismiss', { duration: 7000 });
          }
        },
        error => {
          this.isLocationLoading = false;
          this.currentLatitude = null;
          this.currentLongitude = null;
          console.error('AgroDealerListComponent: Error subscribing to location updates.', error);
          // NEW LOGIC for initial load on error:
          if (!this.initialDealerLoadDone) {
            console.error('AgroDealerListComponent: Error getting initial location. Triggering first loadDealers without filter.');
            this.loadDealers(false); // Load without location filter
            this.initialDealerLoadDone = true;
          }
          this.snackBar.open('Error getting location. Showing dealers without proximity filter.', 'Dismiss', { duration: 7000 });
        }
      );
  }

  private addScrollListener(): void {
    if (!this.scrollListener) {
      this.scrollListener = this.onScroll.bind(this);
      window.addEventListener('scroll', this.scrollListener);
      console.log('Scroll listener added for AgroDealerListComponent.');
    }
  }

  private removeScrollListener(): void {
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener);
      this.scrollListener = null;
      console.log('Scroll listener removed for AgroDealerListComponent.');
    }
  }

  onScroll(event: Event): void {
    const element = event.target as Document;
    const scrollHeight = element.documentElement.scrollHeight;
    const scrollTop = element.documentElement.scrollTop;
    const clientHeight = element.documentElement.clientHeight;

    if (scrollTop + clientHeight >= scrollHeight - 200 && !this.isLoading && this.hasMoreData) {
      this.loadMoreDealers();
    }
  }

  loadDealers(isLoadMore: boolean = false): void {
    if (this.isLoading) return;

    if (!isLoadMore) {
      this.pageIndex = 0;
      this.dealers = [];
      this.hasMoreData = true;
    } else if (!this.hasMoreData) {
      return;
    }

    this.isLoading = true;
    const filters: RetailerFilter = {};

    if (this.searchTerm) {
      filters.searchString = this.searchTerm;
    }

    if (this.showMyDealers) {
      filters.filterByUserId = true;
    }

    if (this.currentLatitude !== null && this.currentLongitude !== null) {
      filters.latitude = this.currentLatitude;
      filters.longitude = this.currentLongitude;
    } else {
        delete filters.latitude;
        delete filters.longitude;
        console.warn('Loading dealers without location filter as current location is not available.');
    }


    this.retailerService.getRetailers(this.pageIndex, this.pageSize, filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        response => {
          this.isLoading = false;
          if (response.data) {
            const newDealers = response.data.retailers || [];
            this.totalDealers = response.data.totalRetailers || 0;

            if (isLoadMore) {
              this.dealers = [...this.dealers, ...newDealers];
            } else {
              this.dealers = newDealers;
            }

            this.hasMoreData = (this.dealers.length < this.totalDealers);
            this.pageIndex++;
          } else {
            this.snackBar.open(response.metaData || 'Failed to fetch agro dealers.', 'Dismiss', { duration: 5000 });
            this.dealers = [];
            this.totalDealers = 0;
            this.hasMoreData = false;
          }
        },
        error => {
          this.isLoading = false;
          this.snackBar.open('Error fetching agro dealers. Please try again later.', 'Dismiss', { duration: 5000 });
          console.error('Error fetching dealers:', error);
          this.dealers = [];
          this.totalDealers = 0;
          this.hasMoreData = false;
        }
      );
  }

  loadMoreDealers(): void {
    if (this.hasMoreData) {
      this.loadDealers(true);
    }
  }

  applySearch(): void {
    this.loadDealers(false);
  }

  onSearchTermChange(): void {
    this.searchInput$.next(this.searchTerm);
  }

  refreshLocation(): void {
    this.dealers = [];
    this.totalDealers = 0;
    this.hasMoreData = false;
    this.pageIndex = 0;
    this.snackBar.open('Refreshing dealer list...', 'Dismiss', { duration: 2000 });
    this.loadDealers(false);
  }

  viewDealerDetails(dealer: RegisteredRetailer): void {
    this.retailerDataStore.setRetailer(dealer);
    this.router.navigate(['/view-dealer-details'], { state: { dealer: dealer } });
  }

  onAddDealer(): void {
    this.retailerDataStore.clearRetailer();
    this.router.navigate(['/add-dealer-phone']);
  }
}